import { useState } from 'react';
import type { Material } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { FileText, Download, ExternalLink, Play, AlertCircle } from 'lucide-react';

interface MediaPlayerProps {
  material: Material;
  height?: string;
  /** API base supplied by application hosts for private object-storage proxying. */
  apiBaseUrl?: string;
}

export function MediaPlayer({ material, height, apiBaseUrl }: MediaPlayerProps) {
  const [hasError, setHasError] = useState(false);

  // Logic to resolve the media URL:
  // Use the primary 'content' field, or fallback to the first matching attachment in the media array
  const rawMediaUrl =
    material.content ||
    material.media?.find(m => {
      if (material.type === 'VIDEO') return m.type === 'VIDEO';
      if (material.type === 'AUDIO' || material.type === 'DISCUSSION') return m.type === 'AUDIO';
      if (material.type === 'DOCUMENT') return m.type === 'PDF';
      return false;
    })?.url ||
    material.media?.[0]?.url;
  const mediaUrl = apiBaseUrl ? resolveMediaUrl(rawMediaUrl, apiBaseUrl) : rawMediaUrl;

  if (!mediaUrl) {
    return (
      <div className="aspect-video bg-surface-container flex flex-col items-center justify-center p-6 text-center border border-on-surface/5">
        <AlertCircle size={40} className="text-warning mb-4" />
        <h3 className="font-headline font-bold text-on-surface mb-2">No Media Available</h3>
        <p className="text-sm text-on-surface-variant max-w-md">
          There is currently no media content linked to this material. Please check back later or
          contact support.
        </p>
      </div>
    );
  }

  // --- VIDEO PLAYER ---
  if (material.type === 'VIDEO') {
    // Check if it's a YouTube link (basic check)
    const isYouTube = mediaUrl.includes('youtube.com') || mediaUrl.includes('youtu.be');

    if (isYouTube) {
      // Very basic extraction, might need a more robust regex in production
      let videoId = '';
      if (mediaUrl.includes('v=')) {
        videoId = mediaUrl.split('v=')[1]?.split('&')[0];
      } else if (mediaUrl.includes('youtu.be/')) {
        videoId = mediaUrl.split('youtu.be/')[1]?.split('?')[0];
      }

      if (videoId) {
        return (
          <div className="relative w-full aspect-video rounded-[2rem] overflow-hidden shadow-level2 bg-black border-4 border-surface-container-lowest">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}?rel=0`}
              title={material.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }
    }

    // Default: Native HTML5 video player for direct media URLs.
    return (
      <div className="relative w-full aspect-video rounded-[2rem] overflow-hidden shadow-level2 bg-surface-container-lowest border border-on-surface/5 group">
        {hasError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface-container text-on-surface-variant p-6 text-center">
            <AlertCircle size={40} className="text-error mb-4" />
            <p className="font-bold mb-1">Failed to load video</p>
            <p className="text-xs">
              The media source might be unavailable or requires authentication.
            </p>
          </div>
        ) : (
          <video
            className="w-full h-full object-contain bg-black"
            controls
            controlsList="nodownload"
            preload="metadata"
            src={mediaUrl}
            onError={() => setHasError(true)}
            poster={`https://images.unsplash.com/photo-1620325867502-221ddb5b4f24?auto=format&fit=crop&w=1200&q=80`} // Abstract gradient placeholder
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    );
  }

  // --- DOCUMENT VIEWER ---
  if (material.type === 'DOCUMENT') {
    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-level1 bg-surface-container-lowest border border-on-surface/5">
        {/* Document Header/Actions */}
        <div className="p-6 border-b border-on-surface/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-primary/5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 text-primary rounded-xl">
              <FileText size={24} />
            </div>
            <div>
              <h3 className="font-headline font-bold text-on-surface text-lg">Document Resource</h3>
              <p className="text-xs text-on-surface-variant">
                Read the material below or download a copy.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors no-underline"
            >
              <ExternalLink size={16} /> Open in New Tab
            </a>
            <a
              href={mediaUrl}
              download
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-on-primary bg-primary hover:bg-primary-container hover:text-on-primary-container rounded-xl transition-colors shadow-sm no-underline"
            >
              <Download size={16} /> Download
            </a>
          </div>
        </div>

        {/* Document Embed (Attempt to show PDF inline) */}
        <div
          className="w-full bg-surface-container-low relative"
          style={{ minHeight: height || '500px' }}
        >
          {hasError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-on-surface-variant">
              <FileText size={48} className="mb-4 opacity-50" />
              <p className="font-bold text-on-surface mb-2">Preview not available</p>
              <p className="text-sm max-w-sm mb-6">
                This document type cannot be previewed directly in the browser. Please download it
                or open it in a new tab.
              </p>
            </div>
          ) : (
            <iframe
              src={`${mediaUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              className="w-full border-none"
              style={{ height: height || '800px' }}
              title={material.title}
              onError={() => setHasError(true)}
              // Fallback content for very old browsers is handled by the container if iframe fails to load
            />
          )}
        </div>
      </div>
    );
  }

  // --- AUDIO PLAYER ---
  if (material.type === 'AUDIO' || material.type === 'DISCUSSION') {
    return (
      <div className="w-full p-10 rounded-[2rem] shadow-level1 bg-surface-container-lowest border border-on-surface/5 flex flex-col items-center gap-8">
        <div className="w-24 h-24 bg-primary/10 rounded-3xl flex items-center justify-center text-primary shadow-inner animate-float">
          <Play size={48} className="fill-primary ml-1" />
        </div>
        <div className="text-center space-y-3">
          <h3 className="font-headline font-bold text-on-surface text-xl">
            {material.type === 'AUDIO' ? 'Audio Resource' : 'Scholarly Discussion'}
          </h3>
          <p className="text-sm text-on-surface-variant max-w-sm mx-auto">
            {material.type === 'AUDIO'
              ? 'Listen to the instructional audio content for this node.'
              : 'Listen to the scholarly discourse for this material.'}
          </p>
        </div>
        <div className="w-full max-w-md bg-surface-container-low/50 p-4 rounded-2xl border border-on-surface/5">
          <audio
            controls
            className="w-full h-10 accent-primary"
            src={mediaUrl}
            onError={() => setHasError(true)}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
        {hasError && (
          <div className="flex items-center gap-2 text-error bg-error/10 px-4 py-2 rounded-full border border-error/20">
            <AlertCircle size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">
              Audio stream unavailable
            </span>
          </div>
        )}
      </div>
    );
  }

  // --- FALLBACK FOR OTHER TYPES (QUIZ, REFLECTION) ---
  return (
    <div className="aspect-video bg-surface-container-lowest flex items-center justify-center p-8 rounded-[2rem] border border-on-surface/5 shadow-sm text-center">
      <div>
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-primary">
          <Play size={32} className="ml-1" />
        </div>
        <h3 className="font-headline font-bold text-on-surface text-xl mb-2">
          Interactive Material
        </h3>
        <p className="text-sm text-on-surface-variant max-w-md mx-auto">
          This material is designed to be interactive. Please scroll down to complete the associated
          tasks or read the description.
        </p>
      </div>
    </div>
  );
}
